import React, { createContext, useState, useEffect, useContext } from "react";
import { getUserRole } from "../services/rbacService";
import { useAuth } from "./AuthContext";

// Create Role Context
export const RoleContext = createContext();

/**
 * RoleProvider Component
 *
 * Manages user role state and provides it to all child components.
 * Fetches role from backend when user is authenticated.
 */
export const RoleProvider = ({ children }) => {
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const fetchRole = async () => {
      if (!isAuthenticated || !user) {
        // User not authenticated, reset role state
        setRole(null);
        setPermissions({});
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // SSO users have their role embedded in the stored profile —
        // skip the /auth/role backend call which requires an MSAL JWT.
        if (user?.source === "sso" && user?.role) {
          setRole(user.role);
          setPermissions(user.permissions || {});
          setLoading(false);
          return;
        }

        const roleData = await getUserRole();

        setRole(roleData.role);
        setPermissions(roleData.permissions);
      } catch (err) {
        console.error("❌ Failed to fetch user role:", err);
        setError(err.message || "Failed to fetch user role");

        // Fail-safe: Default to normal role on error
        setRole("normal");
        setPermissions({
          can_view_overview: false,
          can_view_sales_performance: false,
          can_view_client_insights: true,
          can_view_sentiment: true,
          can_view_my_report: false,
          can_manage_admins: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [isAuthenticated, user]);

  const value = {
    role,
    permissions,
    loading,
    error,
    // Convenience methods
    isNormalUser: role === "normal",
    isAdmin: role === "admin",
    isGlobalAdmin: role === "global_admin",
    hasAdminAccess: role === "admin" || role === "global_admin",
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

/**
 * useRole Hook
 *
 * Custom hook to access role context in any component.
 *
 * Usage:
 *   const { role, permissions, loading } = useRole();
 */
export const useRole = () => {
  const context = useContext(RoleContext);

  if (context === undefined) {
    throw new Error("useRole must be used within a RoleProvider");
  }

  return context;
};
